import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

declare const google: any;

export interface GeocodingResponse {
  placeId: string;
  formattedAddress: string;
  addressComponents: Array<{
    longName: string;
    shortName: string;
    types: string[];
  }>;
}

export interface LatLng {
  latitude: number;
  longitude: number;
}

export class LocationModel {
  latitude: number = 0;
  longitude: number = 0;
  address: string = "";
  city: string = "";
  country: string = "";
  countryCode: string = "";
  placeId: string = "";
  postalCode: string = "";
}

@Injectable({ providedIn: "root" })
export class LocationService {
  private currentLocation: LocationModel = new LocationModel();
  private locationSubject = new BehaviorSubject<LocationModel>(new LocationModel());
  locationObservable: Observable<LocationModel> = this.locationSubject.asObservable();

  getCurrentLocation(): LocationModel {
    return this.currentLocation;
  }

  setCurrentLocation(location: LocationModel): void {
    this.currentLocation = location;
    localStorage.setItem("currentLocation", JSON.stringify(this.currentLocation));
    this.locationSubject.next(this.currentLocation);
  }

  async setCurrentLocationAsync(): Promise<LocationModel> {
    try {
      const storedLocation = localStorage.getItem("currentLocation");
      if (storedLocation) {
        this.currentLocation = JSON.parse(storedLocation);
        this.locationSubject.next(this.currentLocation);
        return this.currentLocation;
      }

      const position = await this.getCurrentPosition();
      const geocodedData = await this.geocode({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      });

      if (geocodedData) {
        this.currentLocation.latitude = position.coords.latitude;
        this.currentLocation.longitude = position.coords.longitude;
        this.currentLocation.address = geocodedData.formattedAddress;
        this.currentLocation.placeId = geocodedData.placeId;

        geocodedData.addressComponents.forEach((component) => {
          const type = component.types[0];
          switch (type) {
            case "postal_code":
              this.currentLocation.postalCode = component.longName;
              break;
            case "country":
              this.currentLocation.country = component.longName;
              this.currentLocation.countryCode = component.shortName;
              break;
            case "locality":
              this.currentLocation.city = component.longName;
              break;
            case "administrative_area_level_1":
            case "administrative_area_level_3":
              if (!this.currentLocation.city) {
                this.currentLocation.city = component.longName;
              }
              break;
            default:
              break;
          }
        });

        localStorage.setItem("currentLocation", JSON.stringify(this.currentLocation));
        this.locationSubject.next(this.currentLocation);
        return this.currentLocation;
      }

      throw new Error("Geocoding failed");
    } catch (error) {
      console.error("Error setting current location:", error);
      return new LocationModel();
    }
  }

  private getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject);
    });
  }

  public geocode({ latitude, longitude }: LatLng): Promise<GeocodingResponse> {
    try {
      if (typeof google === "object" && typeof google.maps === "object") {
        const geocoder = new google.maps.Geocoder();
        const request = { location: new google.maps.LatLng(latitude, longitude) };

        return new Promise((resolve, reject) => {
          geocoder.geocode(request, (results: any, status: any) => {
            if (status === google.maps.GeocoderStatus.OK) {
              resolve(this.formatGeocodeResponse(results[0]));
            } else {
              reject(new Error(`Geocoding failed with status: ${status}`));
            }
          });
        });
      }

      throw new Error("Google Maps script not loaded correctly");
    } catch (error) {
      console.error("Error in geocoder:", error);
      throw error;
    }
  }

  private formatGeocodeResponse(result: any): GeocodingResponse {
    return {
      formattedAddress: result.formatted_address,
      placeId: result.place_id,
      addressComponents: result.address_components.map((component: any) => ({
        longName: component.long_name,
        shortName: component.short_name,
        types: component.types
      }))
    };
  }
}
